// Vibe Radio station picker (contract D.4.2, B.10, C.9). Mode tabs Vibe /
// What came next / Song, shown per features.radio; the vibe list from
// features.vibes; era presets; a soundboard lock (features.sbd); and a song
// select for the walk and song modes. Start hands the params to WEB-CORE,
// which creates the radioSource and plays it.
//
// Props: { band, meta, open, onClose, onStart(radioParams, label) }

import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import * as api from '../api';
import { bandByKey } from '../bands';
import { selectStyles } from '../selectStyles';
import { IconClose, IconPlay, IconMic, IconRadio } from '../icons';
import {
  resolveBand,
  featuresOf,
  erasOf,
  arrowsRare,
  useDismiss,
  useUid,
  arrowNav,
  VIBE_BLURBS,
  ORDER_LABELS,
  ORDER_HINTS,
} from './vibeUtil';
import './vibe.css';

const MODES = [
  { key: 'vibe', label: 'Vibe' },
  { key: 'walk', label: 'What came next' },
  { key: 'song', label: 'Song' },
];

const PATHS = [
  { key: 'classic', label: 'Classic', hint: 'The song they usually played next' },
  { key: 'adventurous', label: 'Adventurous', hint: 'Rarer turns' },
];

// catalog songs per band, fetched once per page load
const songCache = new Map();
const loadSongs = (b) => {
  if (!songCache.has(b.key)) {
    const p = api.fetchCatalogSongs(b).catch((err) => {
      songCache.delete(b.key);
      throw err;
    });
    songCache.set(b.key, p);
  }
  return songCache.get(b.key);
};

const menuStyles = { ...selectStyles, menuPortal: (base) => ({ ...base, zIndex: 1000 }) };

export default function VibeRadioPanel({ band, meta, open, onClose, onStart }) {
  const b = resolveBand(band);
  const bandKey = b ? b.key : null;
  const f = featuresOf(meta);
  const eras = erasOf(meta);
  const rare = arrowsRare(meta);
  const modes = MODES.filter((m) => f.radio[m.key]);
  const vibeKeys = f.vibes.map((v) => v.key).join('|');
  const defaultVibe = f.vibes.some((v) => v.key === 'exploratory') ? 'exploratory' : f.vibes.length ? f.vibes[0].key : null;

  const [mode, setMode] = useState('vibe');
  const [vibe, setVibe] = useState(defaultVibe);
  const [era, setEra] = useState(null);
  const [sbd, setSbd] = useState(false);
  const [song, setSong] = useState(null);
  const [order, setOrder] = useState('chrono');
  const [path, setPath] = useState('classic');
  const [land, setLand] = useState(false);
  const [songs, setSongs] = useState({ key: null, list: [], loading: false, error: null });

  const panelRef = useRef(null);
  const titleId = useUid('vb-radio-title');
  const tabsId = useUid('vb-radio-tab');

  // new band (or a new vibe list): reset the station
  useEffect(() => {
    setMode('vibe');
    setVibe(defaultVibe);
    setEra(null);
    setSbd(false);
    setSong(null);
    setOrder('chrono');
    setPath('classic');
    setLand(false);
  }, [bandKey, vibeKeys, defaultVibe]);

  const activeMode = modes.some((m) => m.key === mode) ? mode : modes.length ? modes[0].key : null;
  const needsSong = activeMode === 'walk' || activeMode === 'song';

  // the song list is only needed for the walk and song modes
  useEffect(() => {
    const bk = bandByKey(bandKey);
    if (!open || !needsSong || !bk || songs.key === bk.key) return undefined;
    let live = true;
    setSongs({ key: null, list: [], loading: true, error: null });
    loadSongs(bk)
      .then((list) => live && setSongs({ key: bk.key, list, loading: false, error: null }))
      .catch((err) => live && setSongs({ key: null, list: [], loading: false, error: err.userMessage || 'Could not load the song list.' }));
    return () => {
      live = false;
    };
  }, [open, needsSong, bandKey, songs.key]);

  useDismiss(open, () => onClose());

  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  if (!b) return null;

  const eraObj = eras.find((e) => e.key === era) || null;
  const vibeObj = f.vibes.find((v) => v.key === vibe) || null;

  const buildParams = () => {
    const p = { mode: activeMode };
    if (era) p.era = era;
    if (f.sbd && sbd) p.sbd = true;
    if (activeMode === 'vibe' && vibeObj) p.vibe = vibeObj.key;
    if (needsSong && song) p.song = song.key;
    if (activeMode === 'walk') p.path = path;
    if (activeMode === 'song') {
      p.order = order;
      if (land && !rare) p.land = true;
    }
    return p;
  };

  const buildLabel = () => {
    if (activeMode === 'walk') return 'What came next: from ' + (song ? song.name : 'a song');
    if (activeMode === 'song') return (song ? song.name : 'Song') + ', ' + ORDER_LABELS[order];
    const parts = [vibeObj ? vibeObj.label : 'All shows', eraObj ? eraObj.label : null, f.sbd && sbd ? 'soundboard' : null];
    return parts.filter(Boolean).join(', ');
  };

  const canStart = !!activeMode && (!needsSong || !!song);

  const start = () => {
    if (!canStart) return;
    const params = buildParams();
    const label = buildLabel();
    onClose();
    onStart(params, label);
  };

  const blurb =
    activeMode === 'vibe'
      ? rare
        ? 'Plays songs from ' + b.name + ' shows with the feel you pick.'
        : 'Plays runs of songs that went into each other, from ' + b.name + ' shows with the feel you pick.'
      : activeMode === 'walk'
      ? 'Starts from a song, then plays what came next on a real night, and keeps going from there.'
      : activeMode === 'song'
      ? 'Plays versions of one song from across the years.'
      : null;

  return (
    <>
      <div className={'panel-overlay' + (open ? ' open' : '')} onClick={onClose} aria-hidden="true" />
      <div
        className={'panel panel-right vb-radio-panel vb-root' + (open ? ' open' : '')}
        role="dialog"
        aria-labelledby={titleId}
        aria-hidden={!open}
        inert={open ? undefined : ''}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="panel-head">
          <div>
            <div className="panel-title" id={titleId}>
              Vibe Radio
            </div>
            <div className="panel-sub">Stations built from {b.name} shows. Use the thumbs while it plays to steer it.</div>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} title="Close" aria-label="Close">
            <IconClose size={18} />
          </button>
        </div>

        <div className="panel-body vb-body">
          {!modes.length ? (
            <div className="vb-empty">Radio is not ready for {b.name} yet.</div>
          ) : (
            <>
              {modes.length > 1 && (
                <div className="vb-seg" role="tablist" aria-label="Station type" onKeyDown={arrowNav}>
                  {modes.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      role="tab"
                      id={tabsId + '-' + m.key}
                      aria-selected={activeMode === m.key}
                      aria-controls={tabsId + '-panel'}
                      tabIndex={activeMode === m.key ? 0 : -1}
                      onClick={() => setMode(m.key)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}

              <div role="tabpanel" id={tabsId + '-panel'} aria-labelledby={tabsId + '-' + activeMode}>
                {blurb && (
                  <p className="vb-note" style={{ margin: '12px 2px 0' }}>
                    {blurb}
                  </p>
                )}

                {activeMode === 'vibe' && f.vibes.length > 0 && (
                  <>
                    <div className="vb-label">Vibe</div>
                    <div className="vb-options" role="radiogroup" aria-label="Vibe" onKeyDown={arrowNav}>
                      {f.vibes.map((v) => (
                        <button
                          key={v.key}
                          type="button"
                          role="radio"
                          aria-checked={vibe === v.key}
                          tabIndex={vibe === v.key || (!vibe && v.key === f.vibes[0].key) ? 0 : -1}
                          className="vb-option"
                          onClick={() => setVibe(v.key)}
                        >
                          <span className="vb-option-dot" aria-hidden="true" />
                          <span className="row-main">
                            <span className="vb-option-title" style={{ display: 'block' }}>
                              {v.label}
                            </span>
                            {VIBE_BLURBS[v.key] && (
                              <span className="vb-option-sub" style={{ display: 'block' }}>
                                {VIBE_BLURBS[v.key]}
                              </span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {needsSong && (
                  <>
                    <div className="vb-label">{activeMode === 'walk' ? 'Start from' : 'Song'}</div>
                    <Select
                      aria-label={activeMode === 'walk' ? 'Start from song' : 'Song'}
                      placeholder={songs.loading ? 'Loading songs' : 'Type a song name'}
                      options={songs.list}
                      value={song}
                      onChange={(o) => setSong(o || null)}
                      styles={menuStyles}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      isLoading={songs.loading}
                      isSearchable
                      isClearable
                    />
                    {songs.error && <div className="vb-banner">{songs.error}</div>}
                  </>
                )}

                {activeMode === 'walk' && (
                  <>
                    <div className="vb-label">Path</div>
                    <div className="vb-seg" role="group" aria-label="Path" onKeyDown={arrowNav}>
                      {PATHS.map((p) => (
                        <button key={p.key} type="button" aria-pressed={path === p.key} title={p.hint} onClick={() => setPath(p.key)}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="vb-note dim" style={{ marginTop: 6 }}>
                      {PATHS.find((p) => p.key === path).hint}
                    </div>
                  </>
                )}

                {activeMode === 'song' && (
                  <>
                    <div className="vb-label">Order</div>
                    <div className="vb-seg" role="group" aria-label="Order" onKeyDown={arrowNav}>
                      {Object.keys(ORDER_LABELS).map((k) => (
                        <button key={k} type="button" aria-pressed={order === k} title={ORDER_HINTS[k]} onClick={() => setOrder(k)}>
                          {ORDER_LABELS[k]}
                        </button>
                      ))}
                    </div>
                    {!rare && (
                      <div style={{ marginTop: 12 }}>
                        <button type="button" role="switch" aria-checked={land} className="vb-switch" onClick={() => setLand(!land)}>
                          <span className="vb-switch-track" aria-hidden="true" />
                          Let it land
                          <span className="dim">keep playing to the end of each run</span>
                        </button>
                      </div>
                    )}
                  </>
                )}

                {eras.length > 0 && (
                  <>
                    <div className="vb-label">Era</div>
                    <div className="vb-chips" role="group" aria-label="Era" onKeyDown={arrowNav}>
                      <button type="button" className="vb-chip" aria-pressed={!era} onClick={() => setEra(null)}>
                        Any era
                      </button>
                      {eras.map((e) => (
                        <button key={e.key} type="button" className="vb-chip" aria-pressed={era === e.key} onClick={() => setEra(e.key)}>
                          {e.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {f.sbd && (
                  <>
                    <div className="vb-label">Sound</div>
                    <button type="button" className="vb-chip" aria-pressed={sbd} onClick={() => setSbd(!sbd)}>
                      <IconMic size={12} /> Soundboard only
                    </button>
                  </>
                )}

                <p className="vb-note dim" style={{ margin: '18px 2px 0' }}>
                  Every song links back to its full show: Open show in the player bar takes you there.
                </p>
              </div>
            </>
          )}
        </div>

        {modes.length > 0 && (
          <div className="vb-panel-foot">
            <IconRadio size={16} className="dim" />
            <span className="vb-foot-label" title={buildLabel()}>
              {canStart ? buildLabel() : 'Pick a song to start'}
            </span>
            <button type="button" className="vb-btn primary" onClick={start} disabled={!canStart}>
              <IconPlay size={13} /> Start radio
            </button>
          </div>
        )}
      </div>
    </>
  );
}
